import {
  ASK_HELIX_INPUT_REQUEST_DTO_FIELDS,
  ASK_HELIX_INPUT_REQUEST_KINDS,
  ASK_HELIX_INPUT_REQUEST_VALUE_TYPES,
  type AskHelixInputRequestDto,
  type AskHelixInputRequestKindDto,
  type AskHelixInputRequestValueTypeDto,
} from './clarification-request';
import {
  ASK_HELIX_INPUT_RESPONSE_DTO_FIELDS,
  type AskHelixInputResponseDto,
  type AskHelixInputResponseValueDto,
  type AskHelixResumeRejectionReasonDto,
} from './clarification-response';
import {
  isPlainDataArray,
  isPlainDataRecord,
  readAllowedOwnStringEntries,
} from './clarification-own-keys';
import { parseClarificationTrustedData } from './clarification-trusted-parse';

export type AskHelixValidationResult<T> =
  | Readonly<{ ok: true; value: T }>
  | Readonly<{ ok: false; reason: AskHelixResumeRejectionReasonDto }>;

type UnknownRecord = Record<string, unknown>;
type OwnDataValues = ReadonlyMap<string, unknown>;

/** Single-sourced from the closed reader allowlist (Law 5) — never a second copy. */
const INPUT_REQUEST_FIELDS: ReadonlySet<string> = new Set<string>(
  ASK_HELIX_INPUT_REQUEST_DTO_FIELDS
);
const INPUT_REQUEST_KINDS: ReadonlySet<AskHelixInputRequestKindDto> = new Set(
  ASK_HELIX_INPUT_REQUEST_KINDS
);

const INPUT_REQUEST_VALUE_TYPES: ReadonlySet<AskHelixInputRequestValueTypeDto> = new Set(
  ASK_HELIX_INPUT_REQUEST_VALUE_TYPES
);

const INPUT_RESPONSE_FIELDS: ReadonlySet<string> = new Set<string>(
  ASK_HELIX_INPUT_RESPONSE_DTO_FIELDS
);

const REQUIRED_INPUT_REQUEST_FIELDS: readonly string[] = [
  'requestId',
  'kind',
  'question',
  'whyItMatters',
  'blocking',
];

const REQUIRED_INPUT_RESPONSE_FIELDS: readonly string[] = [
  'requestId',
  'runId',
  'claimedObjectiveDigest',
  'value',
];

const INPUT_RESPONSE_VALUE_FIELDS: ReadonlySet<string> = new Set<string>([
  'kind',
  'value',
  'option',
  'text',
  'approved',
  'authorized',
]);

export function validateAskHelixInputRequest(
  raw: unknown
): AskHelixValidationResult<AskHelixInputRequestDto> {
  const trusted = parseClarificationTrustedData(raw);
  if (!trusted.ok) return reject('malformed_response');
  const fields = readClosedRecord(
    trusted.value,
    INPUT_REQUEST_FIELDS,
    REQUIRED_INPUT_REQUEST_FIELDS
  );
  if (fields === null) {
    return reject('malformed_response');
  }
  const requestId = fields.get('requestId');
  const kind = fields.get('kind');
  const question = fields.get('question');
  const whyItMatters = fields.get('whyItMatters');
  const blocking = fields.get('blocking');
  const hasExpectedValueType = fields.has('expectedValueType');
  const expectedValueType = fields.get('expectedValueType');
  const hasUnit = fields.has('unit');
  const unit = fields.get('unit');
  const hasOptions = fields.has('options');
  const options = hasOptions ? readInputRequestOptions(fields.get('options')) : undefined;

  if (!isNonBlankString(requestId)) return reject('malformed_response');
  if (!isInputRequestKind(kind)) return reject('malformed_response');
  if (!isNonBlankString(question) || !isNonBlankString(whyItMatters)) {
    return reject('malformed_response');
  }
  if (typeof blocking !== 'boolean') return reject('malformed_response');
  let validatedExpectedValueType: AskHelixInputRequestValueTypeDto | undefined;
  if (hasExpectedValueType) {
    if (!isInputValueType(expectedValueType)) return reject('malformed_response');
    validatedExpectedValueType = expectedValueType;
  }
  let validatedUnit: string | undefined;
  if (hasUnit) {
    if (typeof unit !== 'string') return reject('malformed_response');
    validatedUnit = unit;
  }
  if (hasOptions && options === null) return reject('malformed_response');
  if (!validRequestShape(kind, hasExpectedValueType, hasOptions)) {
    return reject('malformed_response');
  }

  return {
    ok: true,
    value: inputRequestFromValues({
      requestId,
      kind,
      question,
      whyItMatters,
      expectedValueType: validatedExpectedValueType,
      unit: validatedUnit,
      options: options ?? undefined,
      blocking,
    }),
  };
}

export function validateAskHelixInputResponse(
  raw: unknown,
  offeredRequest?: AskHelixInputRequestDto
): AskHelixValidationResult<AskHelixInputResponseDto> {
  const trusted = parseClarificationTrustedData(raw);
  if (!trusted.ok) return reject('malformed_response');
  const trustedOffered =
    offeredRequest === undefined ? undefined : parseClarificationTrustedData(offeredRequest);
  if (trustedOffered !== undefined && !trustedOffered.ok) {
    return reject('unknown_pending_request');
  }
  const fields = readClosedRecord(
    trusted.value,
    INPUT_RESPONSE_FIELDS,
    REQUIRED_INPUT_RESPONSE_FIELDS
  );
  if (fields === null) {
    return reject('malformed_response');
  }
  const requestId = fields.get('requestId');
  const runId = fields.get('runId');
  const claimedObjectiveDigest = fields.get('claimedObjectiveDigest');
  const rawValue = fields.get('value');
  if (!isNonBlankString(requestId) || !isNonBlankString(runId)) {
    return reject('malformed_response');
  }
  if (!isNonBlankString(claimedObjectiveDigest)) return reject('malformed_response');
  const offered =
    trustedOffered === undefined ? undefined : readOfferedRequest(trustedOffered.value);
  if (offeredRequest !== undefined && offered === null) return reject('unknown_pending_request');
  if (offered && requestId !== offered.requestId) {
    return reject('unknown_pending_request');
  }
  const parsedValue = parseInputResponseValue(rawValue);
  if (parsedValue.value === null) return reject(parsedValue.reason);
  const value = parsedValue.value;
  if (value.kind === 'objective_revision') {
    return reject('objective_revision_not_supported');
  }
  if (offered) {
    const mismatch = responseMismatchReason(value, offered);
    if (mismatch) return reject(mismatch);
  }

  return {
    ok: true,
    value: {
      requestId,
      runId,
      claimedObjectiveDigest,
      value,
    },
  };
}

function inputRequestFromValues(raw: {
  readonly requestId: string;
  readonly kind: AskHelixInputRequestKindDto;
  readonly question: string;
  readonly whyItMatters: string;
  readonly expectedValueType?: AskHelixInputRequestValueTypeDto;
  readonly unit?: string;
  readonly options?: readonly string[];
  readonly blocking: boolean;
}): AskHelixInputRequestDto {
  const request: {
    requestId: string;
    kind: AskHelixInputRequestKindDto;
    question: string;
    whyItMatters: string;
    expectedValueType?: AskHelixInputRequestValueTypeDto;
    unit?: string;
    options?: readonly string[];
    blocking: boolean;
  } = {
    requestId: raw.requestId,
    kind: raw.kind,
    question: raw.question,
    whyItMatters: raw.whyItMatters,
    blocking: raw.blocking,
  };
  if (raw.expectedValueType !== undefined) {
    request.expectedValueType = raw.expectedValueType;
  }
  if (raw.unit !== undefined) request.unit = raw.unit;
  if (raw.options !== undefined) {
    request.options = copyStrings(raw.options);
  }
  return request;
}

function copyStrings(values: readonly string[]): string[] {
  const copied: string[] = [];
  for (let index = 0; index < values.length; index += 1) {
    copied.push(values[index]);
  }
  return copied;
}

/**
 * Mirrors the per-kind shape rules at material-input-validator.ts:67
 * (apps/api/src/ask-helix/manager-runtime/loop/material-input/). Its evidence_upload
 * authority.evidenceKey rule is not expressible on this eight-field reader DTO.
 */
function validRequestShape(
  kind: AskHelixInputRequestKindDto,
  hasExpectedValueType: boolean,
  hasOptions: boolean
): boolean {
  switch (kind) {
    case 'typed_value':
      return hasExpectedValueType && !hasOptions;
    case 'policy_choice':
      return hasOptions;
    case 'clarification':
    case 'evidence_upload':
    case 'assumption_authorization':
    case 'approval':
      return !hasOptions;
    default:
      return false;
  }
}

/**
 * `options` is the DTO's only non-scalar field, so it is fenced directly rather than by a
 * general recursive constraint. The prototype is pinned and iteration goes through the
 * intrinsic `Array.prototype.every` — a caller-supplied prototype with an overridden `every`
 * must not be able to vouch for its own elements.
 */
export function isAskHelixInputRequestOptions(value: unknown): value is readonly string[] {
  const trusted = parseClarificationTrustedData(value);
  return trusted.ok && readInputRequestOptions(trusted.value) !== null;
}

export function isPlainArray(value: unknown): value is readonly unknown[] {
  const trusted = parseClarificationTrustedData(value);
  return trusted.ok && readPlainArrayValues(trusted.value) !== null;
}

function responseMismatchReason(
  value: Exclude<AskHelixInputResponseValueDto, Readonly<{ kind: 'objective_revision' }>>,
  request: Readonly<{
    requestId: string;
    kind: AskHelixInputRequestKindDto;
    expectedValueType?: AskHelixInputRequestValueTypeDto;
    options?: readonly string[];
  }>
): AskHelixResumeRejectionReasonDto | null {
  if (request.kind === 'evidence_upload') return 'evidence_upload_not_supported_here';
  if (expectedResponseKind(request.kind) !== value.kind) return 'response_kind_mismatch';
  if (value.kind === 'typed_value' && request.expectedValueType) {
    return matchesExpectedValueType(value.value, request.expectedValueType)
      ? null
      : 'response_kind_mismatch';
  }
  if (
    value.kind === 'policy_choice' &&
    !request.options?.some((option) => option === value.option)
  ) {
    return 'response_option_not_offered';
  }
  return null;
}

function parseInputResponseValue(raw: unknown): Readonly<{
  value: AskHelixInputResponseValueDto | null;
  reason: AskHelixResumeRejectionReasonDto;
}> {
  const fields = readClosedRecord(raw, INPUT_RESPONSE_VALUE_FIELDS, ['kind']);
  if (fields === null) return malformedResponseValue();
  const kind = fields.get('kind');
  if (typeof kind !== 'string') return malformedResponseValue();
  switch (kind) {
    case 'typed_value': {
      const value = fields.get('value');
      return hasExactFields(fields, ['kind', 'value']) && isScalarValue(value)
        ? { value: { kind: 'typed_value', value }, reason: 'malformed_response' }
        : malformedResponseValue();
    }
    case 'policy_choice': {
      const option = fields.get('option');
      return hasExactFields(fields, ['kind', 'option']) && isNonBlankString(option)
        ? { value: { kind: 'policy_choice', option }, reason: 'malformed_response' }
        : malformedResponseValue();
    }
    case 'clarification': {
      const text = fields.get('text');
      return hasExactFields(fields, ['kind', 'text']) && typeof text === 'string'
        ? { value: { kind: 'clarification', text }, reason: 'malformed_response' }
        : malformedResponseValue();
    }
    case 'approval': {
      const approved = fields.get('approved');
      return hasExactFields(fields, ['kind', 'approved']) && typeof approved === 'boolean'
        ? { value: { kind: 'approval', approved }, reason: 'malformed_response' }
        : responseKindMismatch();
    }
    case 'assumption_authorization': {
      const authorized = fields.get('authorized');
      return hasExactFields(fields, ['kind', 'authorized']) && typeof authorized === 'boolean'
        ? {
            value: { kind: 'assumption_authorization', authorized },
            reason: 'malformed_response',
          }
        : responseKindMismatch();
    }
    case 'objective_revision':
      return hasExactFields(fields, ['kind'])
        ? { value: { kind: 'objective_revision' }, reason: 'malformed_response' }
        : malformedResponseValue();
    default:
      return malformedResponseValue();
  }
}

function malformedResponseValue(): Readonly<{
  value: null;
  reason: 'malformed_response';
}> {
  return { value: null, reason: 'malformed_response' };
}

function responseKindMismatch(): Readonly<{
  value: null;
  reason: 'response_kind_mismatch';
}> {
  return { value: null, reason: 'response_kind_mismatch' };
}

function readOfferedRequest(raw: unknown): Readonly<{
  requestId: string;
  kind: AskHelixInputRequestKindDto;
  expectedValueType?: AskHelixInputRequestValueTypeDto;
  options?: readonly string[];
}> | null {
  const fields = readClosedRecord(raw, INPUT_REQUEST_FIELDS, REQUIRED_INPUT_REQUEST_FIELDS);
  if (fields === null) return null;
  const requestId = fields.get('requestId');
  const kind = fields.get('kind');
  const expectedValueType = fields.get('expectedValueType');
  const rawOptions = fields.get('options');
  if (!isNonBlankString(requestId) || !isInputRequestKind(kind)) return null;
  let safeExpectedValueType: AskHelixInputRequestValueTypeDto | undefined;
  if (expectedValueType !== undefined) {
    if (!isInputValueType(expectedValueType)) return null;
    safeExpectedValueType = expectedValueType;
  }
  const options = rawOptions === undefined ? undefined : readInputRequestOptions(rawOptions);
  if (options === null) return null;
  return {
    requestId,
    kind,
    expectedValueType: safeExpectedValueType,
    options,
  };
}

function expectedResponseKind(
  kind: AskHelixInputRequestKindDto
): Exclude<AskHelixInputResponseValueDto['kind'], 'objective_revision'> | null {
  switch (kind) {
    case 'clarification':
      return 'clarification';
    case 'typed_value':
      return 'typed_value';
    case 'policy_choice':
      return 'policy_choice';
    case 'evidence_upload':
      return null;
    case 'assumption_authorization':
      return 'assumption_authorization';
    case 'approval':
      return 'approval';
    default:
      return null;
  }
}

function matchesExpectedValueType(
  value: string | number | boolean,
  expected: AskHelixInputRequestValueTypeDto
): boolean {
  switch (expected) {
    case 'string':
    case 'number':
    case 'boolean':
      return typeof value === expected;
    case 'file':
      return false;
    default:
      return false;
  }
}

function isInputRequestKind(value: unknown): value is AskHelixInputRequestKindDto {
  return typeof value === 'string' && INPUT_REQUEST_KINDS.has(value as AskHelixInputRequestKindDto);
}

function isInputValueType(value: unknown): value is AskHelixInputRequestValueTypeDto {
  return (
    typeof value === 'string' &&
    INPUT_REQUEST_VALUE_TYPES.has(value as AskHelixInputRequestValueTypeDto)
  );
}

function readClosedRecord(
  value: unknown,
  fields: ReadonlySet<string>,
  requiredFields: readonly string[]
): OwnDataValues | null {
  if (!isPlainRecord(value)) return null;
  const entries = readAllowedOwnStringEntries(value, (key) => fields.has(key));
  if (entries === null) return null;
  const values: OwnDataValues = new Map(entries);
  return requiredFields.every((field) => values.has(field)) ? values : null;
}

function hasExactFields(values: OwnDataValues, fields: readonly string[]): boolean {
  return values.size === fields.length && fields.every((field) => values.has(field));
}

function isPlainRecord(value: unknown): value is UnknownRecord {
  return isPlainDataRecord(value);
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function readPlainArrayValues(value: unknown): readonly unknown[] | null {
  if (!isPlainDataArray(value)) return null;
  const entries = readAllowedOwnStringEntries(
    value,
    (key) => key === 'length' || isArrayIndexKey(key),
    (key) => key === 'length'
  );
  if (entries === null) return null;
  const values: OwnDataValues = new Map(entries);
  const length = values.get('length');
  if (!isArrayLength(length) || values.size !== length + 1) return null;

  const elements: unknown[] = [];
  for (let index = 0; index < length; index += 1) {
    const key = String(index);
    if (!values.has(key)) return null;
    elements.push(values.get(key));
  }
  return elements;
}

function readInputRequestOptions(value: unknown): readonly string[] | null {
  const options = readPlainArrayValues(value);
  if (options === null || options.length === 0 || !options.every(isString)) return null;
  return options;
}

function isArrayIndexKey(key: string): boolean {
  const index = Number(key);
  return Number.isInteger(index) && index >= 0 && index < 2 ** 32 - 1 && String(index) === key;
}

function isArrayLength(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isScalarValue(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function reject<T>(reason: AskHelixResumeRejectionReasonDto): AskHelixValidationResult<T> {
  return { ok: false, reason };
}
